import { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { getMyPageSettings, updateMyPageSettings } from 'src/shared/services/regSettingService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RegSettingDetail } from '../models/regSetting';

// Antal erbjudanden (matchar FS 1.0: offer1..offer5).
const OFFER_COUNT = 5;
const OFFER_NUMBERS = Array.from({ length: OFFER_COUNT }, (_, i) => i + 1);

interface MyPageForm {
  [key: string]: string | boolean;
  enableBankidLogin: boolean;
  enableOffer: boolean;
  offerName: string;
}

// reg_settings-nycklar (kategori "mypage") – samma som FS 1.0 så vyn binder mot befintlig data.
const KNOWN_FIELDS: { keyName: string; type: 'varchar' | 'bit' | 'text' }[] = [
  { keyName: 'enableBankidLogin', type: 'bit' },
  { keyName: 'enableOffer', type: 'bit' },
  { keyName: 'offerName', type: 'varchar' },
  ...OFFER_NUMBERS.flatMap((n) => [
    { keyName: `offer${n}`, type: 'varchar' as const },
    { keyName: `offer${n}URL`, type: 'varchar' as const },
    { keyName: `offer${n}Open`, type: 'bit' as const },
  ]),
];

const buildDefaults = (): MyPageForm => {
  const defaults = {} as MyPageForm;
  KNOWN_FIELDS.forEach((f) => {
    defaults[f.keyName] = f.type === 'bit' ? false : '';
  });
  return defaults;
};

const mapSettingsToForm = (settings: RegSettingDetail[]): MyPageForm => {
  const defaults = buildDefaults();
  settings.forEach((s) => {
    if (s.keyName in defaults) {
      if (s.field === 'bit') {
        defaults[s.keyName] = !!s.bit;
      } else if (s.field === 'text') {
        defaults[s.keyName] = s.text ?? '';
      } else {
        defaults[s.keyName] = s.varchar ?? '';
      }
    }
  });
  return defaults;
};

const mapFormToUpdates = (formData: MyPageForm, settings: RegSettingDetail[]): RegSettingDetail[] => {
  const updates = settings.map((s) => {
    const knownField = KNOWN_FIELDS.find((f) => f.keyName === s.keyName);
    if (knownField && s.keyName in formData) {
      return {
        ...s,
        varchar: knownField.type === 'varchar' ? (formData[s.keyName] as string) : s.varchar,
        bit: knownField.type === 'bit' ? (formData[s.keyName] as boolean) : s.bit,
        text: knownField.type === 'text' ? (formData[s.keyName] as string) : s.text,
      };
    }
    return s;
  });

  // Skapa nya rader (kategori "mypage") för nycklar som saknas i databasen.
  KNOWN_FIELDS.forEach((kf) => {
    if (!settings.some((s) => s.keyName === kf.keyName)) {
      updates.push({
        // eslint-disable-next-line camelcase
        settings_id: 0,
        // eslint-disable-next-line camelcase
        product_id: 0,
        category: 'mypage',
        keyName: kf.keyName,
        field: kf.type,
        varchar: kf.type === 'varchar' ? (formData[kf.keyName] as string) : null,
        integer: 0,
        smallint: 0,
        bit: kf.type === 'bit' ? (formData[kf.keyName] as boolean) : null,
        text: kf.type === 'text' ? (formData[kf.keyName] as string) : null,
      });
    }
  });

  return updates;
};

export const MemberRegistrySettingsMyPagePage = () => {
  const { t } = useTranslation(['settings', 'common']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<RegSettingDetail[]>([]);

  const loginUrl = `https://app.foreningssupport.se/login?tenant=${currentUser?.custName ?? ''}`;

  const formMethods = useForm<MyPageForm>({ mode: 'all', defaultValues: buildDefaults() });
  const { handleSubmit, reset, watch } = formMethods;

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMyPageSettings();
      if (isMounted()) {
        setSettings(result.data);
        reset(mapSettingsToForm(result.data));
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, reset]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const onSubmit = useCallback(
    async (formData: MyPageForm) => {
      setSaving(true);
      try {
        await updateMyPageSettings(mapFormToUpdates(formData, settings));
        if (isMounted()) {
          toast.success(t('settings:memberRegistry.saveSuccess') as string);
          fetchSettings();
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          toast.error(t('settings:memberRegistry.saveError') as string);
        }
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [isMounted, settings, t, fetchSettings],
  );

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(loginUrl);
    toast.success(t('settings:memberRegistry.myPage.linkCopied') as string);
  };

  const showOffers = watch('enableOffer');

  return (
    <Box sx={{ position: 'relative', minHeight: loading ? 200 : 'auto' }}>
      <LoadingOverlay loading={loading} />
      {!loading && (
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container sx={{ p: 3 }}>
              {/* Inloggningslänk */}
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('settings:memberRegistry.myPage.loginLinkTitle')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('settings:memberRegistry.myPage.loginLinkDescription')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: 14,
                      wordBreak: 'break-all',
                    }}
                  >
                    {loginUrl}
                  </Box>
                  <Button size='small' startIcon={<ContentCopy />} onClick={handleCopyLink} sx={{ mt: 1 }}>
                    {t('settings:memberRegistry.myPage.copyLink')}
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Divider />
              </Grid>

              {/* Inloggning (BankID) */}
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('settings:memberRegistry.myPage.loginTitle')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('settings:memberRegistry.myPage.loginDescription')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ControlledCheckboxNew name='enableBankidLogin' label={t('settings:memberRegistry.myPage.enableBankidLogin')} />
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Divider />
              </Grid>

              {/* Mina erbjudanden */}
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('settings:memberRegistry.myPage.offersTitle')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('settings:memberRegistry.myPage.offersDescription')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ControlledCheckboxNew name='enableOffer' label={t('settings:memberRegistry.myPage.enableOffer')} />
                    </Grid>
                    {showOffers && (
                      <>
                        <Grid item xs={12}>
                          <ControlledTextField name='offerName' label={t('settings:memberRegistry.myPage.offerName')} size='small' fullWidth />
                        </Grid>
                        {OFFER_NUMBERS.map((n) => (
                          <Grid item xs={12} key={`offer-${n}`}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                              {t('settings:memberRegistry.myPage.offerLabel', { number: n })}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <ControlledTextField
                                  name={`offer${n}`}
                                  label={t('settings:memberRegistry.myPage.offerText')}
                                  size='small'
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <ControlledTextField
                                  name={`offer${n}URL`}
                                  label={t('settings:memberRegistry.myPage.offerUrl')}
                                  size='small'
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <ControlledCheckboxNew name={`offer${n}Open`} label={t('settings:memberRegistry.myPage.offerOpen')} />
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button type='submit' variant='contained' disabled={saving}>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      )}
    </Box>
  );
};
